import { useEffect, useRef, useState } from "react";
import {
  Bell,
  Check,
  ChevronDown,
  ClipboardList,
  Clock,
  GripVertical,
  StickyNote,
  TrendingUp,
  X,
} from "lucide-react";
import type { SectionId } from "../../hooks/useStickyBulletin";
import { DropdownSurface } from "../shared/DropdownSurface";

const SECTIONS = [
  { id: "inbox" as const, icon: Bell, label: "Inbox" },
  { id: "volwatch" as const, icon: Clock, label: "VolWatch" },
  { id: "notes" as const, icon: StickyNote, label: "Notes" },
  { id: "upcoming" as const, icon: TrendingUp, label: "Upcoming" },
];

export function BulletinHeader({
  isMobile,
  isQuickClockVisible,
  isQuickClockActive,
  onQuickClock,
  onClose,
  onBeginDrag,
}: {
  isMobile: boolean;
  isQuickClockVisible: boolean;
  isQuickClockActive: boolean;
  onQuickClock: () => void;
  onClose: () => void;
  onBeginDrag: (event: React.MouseEvent) => void;
}) {
  return (
    <div
      className={`flex items-center justify-between px-4 py-3 ${isMobile ? "cursor-default" : "cursor-move"}`}
      onMouseDown={onBeginDrag}
    >
      <div className="flex items-center gap-2">
        {!isMobile ? (
          <span
            aria-hidden
            className="cursor-grab p-0.5 text-[var(--fintheon-accent)]/40"
          >
            <GripVertical className="h-3.5 w-3.5" />
          </span>
        ) : null}
        <ClipboardList className="h-4 w-4 text-[var(--fintheon-accent)]" />
        <span className="text-[12px] font-semibold tracking-[0.12em] text-[var(--fintheon-accent)]">
          Bulletin
        </span>
      </div>
      <div className="flex items-center gap-1">
        {isQuickClockVisible ? (
          <button
            type="button"
            onClick={onQuickClock}
            onMouseDown={(event) => event.stopPropagation()}
            className="rounded-md p-1 text-[var(--fintheon-muted)] transition-colors hover:text-[var(--fintheon-accent)] active:scale-90"
            title="Quick VolWatch observation"
            aria-label="Quick VolWatch observation"
          >
            <Clock
              className={`h-3.5 w-3.5 ${isQuickClockActive ? "text-[var(--fintheon-accent)]" : ""}`}
            />
          </button>
        ) : null}
        <button
          type="button"
          onClick={onClose}
          onMouseDown={(event) => event.stopPropagation()}
          className="rounded-md p-1 text-[var(--fintheon-muted)] transition-colors hover:text-[var(--fintheon-text)]"
          aria-label="Close bulletin"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

export function BulletinSectionNavigation({
  isMobile,
  activeSection,
  onSelect,
}: {
  isMobile: boolean;
  activeSection: SectionId;
  onSelect: (section: SectionId) => void;
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        surfaceRef.current?.contains(target)
      )
        return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [isOpen]);

  if (isMobile) {
    return (
      <div className="px-3 py-2">
        <button
          ref={triggerRef}
          type="button"
          aria-label="Bulletin section"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((value) => !value)}
          className="bulletin-section-trigger riskflow-soft-action flex h-9 w-full items-center justify-between rounded-lg px-3 text-[11px] font-medium tracking-[0.04em] text-[var(--fintheon-accent)] outline-none"
        >
          <span>
            {SECTIONS.find((section) => section.id === activeSection)?.label}
          </span>
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </button>
        <DropdownSurface
          open={isOpen}
          anchorRef={triggerRef}
          surfaceRef={surfaceRef}
          align="left"
          minWidth={176}
          matchAnchorWidth={false}
          className="riskflow-unified-filter-menu bulletin-section-menu"
        >
          <div className="px-1.5 py-1">
            {SECTIONS.map((section) => (
              <button
                key={section.id}
                type="button"
                onClick={() => {
                  onSelect(section.id);
                  setIsOpen(false);
                }}
                className={`flex w-full items-center gap-1.5 px-2 py-1 text-[10px] tracking-[0.04em] transition-colors ${activeSection === section.id ? "text-[var(--fintheon-accent)]" : "text-[var(--fintheon-text)]/58 hover:text-[var(--fintheon-text)]"}`}
              >
                <span className="inline-flex h-3 w-3 items-center justify-center">
                  {activeSection === section.id ? (
                    <Check className="h-2.5 w-2.5" />
                  ) : null}
                </span>
                <section.icon className="h-3.5 w-3.5" />
                <span>{section.label}</span>
              </button>
            ))}
          </div>
        </DropdownSurface>
      </div>
    );
  }

  return (
    <div className="flex gap-0.5 px-2 py-1.5">
      {SECTIONS.map((section) => {
        const isActive = activeSection === section.id;
        return (
          <button
            key={section.id}
            type="button"
            onClick={() => onSelect(section.id)}
            className={`flex flex-1 items-center justify-center gap-1 rounded-lg py-1.5 text-[9px] uppercase tracking-wide transition-colors ${isActive ? "bg-[var(--fintheon-accent)]/10 text-[var(--fintheon-accent)]" : "text-[var(--fintheon-muted)] hover:text-[var(--fintheon-text)]"}`}
          >
            <section.icon className="h-3 w-3" />
            <span>{section.label}</span>
          </button>
        );
      })}
    </div>
  );
}
